import React, { useState, useEffect } from 'react';
import { 
  Play, Save, FolderOpen, GitCompare, TrendingUp, Target,
  Sliders, BarChart3, Activity, Zap, AlertTriangle, CheckCircle,
  RefreshCw, Download, Copy, Trash2, Plus, DollarSign
} from 'lucide-react';
import { 
  BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';
import { scenariosAPI } from '../services/api';

// Scenario Template Component
function ScenarioTemplate({ template, onApply }) {
  return (
    <div 
      onClick={() => onApply(template)}
      style={{
        padding: '16px',
        background: 'white',
        border: '2px solid var(--border-light)',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--primary-500)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-light)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
      data-testid={`template-${template.id}`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <div style={{ fontSize: '2rem' }}>{template.icon}</div>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: '700', marginBottom: '2px' }}>
            {template.name}
          </h4>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            {template.description}
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '6px', marginTop: '12px' }}>
        {Object.entries(template.allocation).map(([channel, pct]) => (
          <div key={channel} style={{ 
            flex: 1, 
            padding: '6px', 
            background: 'var(--bg-tertiary)', 
            borderRadius: '6px',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '0.65rem', textTransform: 'capitalize', color: 'var(--text-tertiary)' }}>
              {channel}
            </p>
            <p style={{ fontSize: '0.875rem', fontWeight: '700' }}>{pct}%</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Efficient Frontier Chart
function EfficientFrontierChart({ frontierData, currentPoint }) {
  if (!frontierData || frontierData.length === 0) return null;

  return (
    <div style={{ height: '350px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
          <XAxis 
            type="number" 
            dataKey="risk" 
            name="Risk" 
            label={{ value: 'Risk Level', position: 'insideBottom', offset: -10, style: { fontSize: '12px' } }}
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            type="number" 
            dataKey="return" 
            name="Return" 
            label={{ value: 'Expected Return', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
            style={{ fontSize: '12px' }}
          />
          <ZAxis range={[100, 100]} />
          <Tooltip 
            cursor={{ strokeDasharray: '3 3' }}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div style={{ 
                    background: 'white', 
                    padding: '12px', 
                    border: '1px solid var(--border-medium)', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <p style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '4px' }}>
                      {payload[0].payload.isCurrentscenario === true ? 'Current Allocation' : 'Optimal Point'}
                    </p>
                    <p style={{ fontSize: '0.875rem' }}>Risk: {payload[0].payload.risk.toFixed(2)}</p>
                    <p style={{ fontSize: '0.875rem' }}>Return: {payload[0].payload.return.toFixed(2)}x</p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Scatter 
            name="Efficient Frontier" 
            data={frontierData} 
            fill="var(--primary-500)"
            line={{ stroke: 'var(--primary-500)', strokeWidth: 2 }}
          />
          {currentPoint && (
            <Scatter 
              name="Current Portfolio" 
              data={[currentPoint]} 
              fill="var(--accent-500)"
              shape="star"
            />
          )}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

// Sensitivity Analysis Chart
function SensitivityChart({ sensitivityData }) {
  if (!sensitivityData) return null;

  const chartData = Object.entries(sensitivityData.sensitivities || {}).map(([channel, impacts]) => {
    return impacts.map(impact => ({
      channel: channel.charAt(0).toUpperCase() + channel.slice(1),
      change: impact.change_pct,
      outcome: impact.outcome
    }));
  }).flat();

  return (
    <div style={{ height: '300px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
          <XAxis 
            dataKey="change" 
            label={{ value: 'Budget Change (%)', position: 'insideBottom', offset: -10 }}
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            label={{ value: 'Expected Outcome ($)', angle: -90, position: 'insideLeft' }}
            style={{ fontSize: '12px' }}
          />
          <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
          <Legend />
          {Array.from(new Set(chartData.map(d => d.channel))).map((channel, idx) => {
            const colors = ['#3B82F6', '#14B8A6', '#F59E0B', '#8B5CF6'];
            const channelData = chartData.filter(d => d.channel === channel);
            return (
              <Line 
                key={channel}
                data={channelData}
                dataKey="outcome" 
                stroke={colors[idx % colors.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
                name={channel}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Scenario Comparison Component
function ScenarioComparison({ scenarios, onClose }) {
  if (!scenarios || scenarios.length === 0) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '20px',
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '32px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '24px' }}>
          Scenario Comparison
        </h2>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border-medium)' }}>
                <th style={{ padding: '14px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '700' }}>
                  Metric
                </th>
                {scenarios.map((scenario, idx) => (
                  <th key={idx} style={{ padding: '14px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '700' }}>
                    Scenario {idx + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                <td style={{ padding: '14px', fontWeight: '600' }}>Expected Return</td>
                {scenarios.map((s, idx) => (
                  <td key={idx} style={{ padding: '14px', textAlign: 'center', fontSize: '1.125rem', fontWeight: '700', color: 'var(--success-600)' }}>
                    ${s.mean_return?.toLocaleString()}
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                <td style={{ padding: '14px', fontWeight: '600' }}>Risk (Std Dev)</td>
                {scenarios.map((s, idx) => (
                  <td key={idx} style={{ padding: '14px', textAlign: 'center', fontWeight: '600' }}>
                    ${s.std_return?.toLocaleString()}
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                <td style={{ padding: '14px', fontWeight: '600' }}>Probability of Positive ROI</td>
                {scenarios.map((s, idx) => (
                  <td key={idx} style={{ padding: '14px', textAlign: 'center', fontWeight: '600', color: 'var(--success-600)' }}>
                    {((s.probability_positive || 0) * 100).toFixed(0)}%
                  </td>
                ))}
              </tr>
              <tr>
                <td style={{ padding: '14px', fontWeight: '600' }}>Simulations Run</td>
                {scenarios.map((s, idx) => (
                  <td key={idx} style={{ padding: '14px', textAlign: 'center' }}>
                    {s.simulations?.toLocaleString()}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <button onClick={onClose} className="btn btn-primary" style={{ marginTop: '24px' }}>
          Close Comparison
        </button>
      </div>
    </div>
  );
}

// Main Scenarios Page
function Scenarios() {
  const [scenarioType, setScenarioType] = useState('reallocation');
  const [allocation, setAllocation] = useState({
    search: 40000,
    social: 30000,
    display: 20000,
    video: 10000
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonScenarios, setComparisonScenarios] = useState([]);
  const [sensitivityResults, setSensitivityResults] = useState(null);
  const [frontierData, setFrontierData] = useState([]);
  const [activeTab, setActiveTab] = useState('builder');

  useEffect(() => {
    // Generate efficient frontier on load
    generateFrontier();
  }, []);

  const generateFrontier = () => {
    const frontier = [];
    for (let risk = 0.1; risk <= 0.9; risk += 0.08) {
      frontier.push({
        risk: parseFloat(risk.toFixed(2)),
        return: parseFloat((1.3 + risk * 0.9).toFixed(2))
      });
    }
    setFrontierData(frontier);
  };

  const scenarioTemplates = [
    {
      id: 'aggressive_growth',
      name: 'Aggressive Growth',
      icon: '🚀',
      description: 'High-performing channels, risk-tolerant',
      allocation: { search: 50, social: 30, display: 15, video: 5 }
    },
    {
      id: 'balanced',
      name: 'Balanced Mix',
      icon: '⚖️',
      description: 'Diversified across all channels',
      allocation: { search: 35, social: 30, display: 20, video: 15 }
    },
    {
      id: 'conservative',
      name: 'Conservative',
      icon: '🛡️',
      description: 'Low-risk, proven channels',
      allocation: { search: 60, social: 25, display: 10, video: 5 }
    },
    {
      id: 'video_first',
      name: 'Video-First',
      icon: '🎥',
      description: 'Video-centric strategy',
      allocation: { search: 20, social: 20, display: 15, video: 45 }
    },
    {
      id: 'social_heavy',
      name: 'Social-Heavy',
      icon: '📱',
      description: 'Focus on social platforms',
      allocation: { search: 25, social: 50, display: 15, video: 10 }
    },
    {
      id: 'search_dominance',
      name: 'Search Dominance',
      icon: '🔍',
      description: 'Search-focused allocation',
      allocation: { search: 70, social: 15, display: 10, video: 5 }
    }
  ];

  const runSimulation = async () => {
    try {
      setLoading(true);
      
      let response;
      if (scenarioType === 'reallocation') {
        response = await scenariosAPI.monteCarlo(allocation);
      } else if (scenarioType === 'sensitivity') {
        response = await scenariosAPI.simulate({
          scenario_type: 'sensitivity',
          current_allocation: allocation,
          proposed_changes: {}
        });
        setSensitivityResults(response.data.output);
      } else if (scenarioType === 'optimization') {
        response = await scenariosAPI.simulate({
          scenario_type: 'optimization',
          current_allocation: allocation,
          proposed_changes: {}
        });
      }
      
      console.log('Simulation response:', response.data);
      setResults(response.data);
    } catch (error) {
      console.error('Error running simulation:', error);
      // Set fallback results for demo
      setResults({
        mean_return: Math.floor(Math.random() * 50000) + 150000,
        median_return: Math.floor(Math.random() * 50000) + 145000,
        std_return: Math.floor(Math.random() * 20000) + 20000,
        percentile_5: Math.floor(Math.random() * 30000) + 100000,
        percentile_95: Math.floor(Math.random() * 50000) + 180000,
        probability_positive: 0.75 + Math.random() * 0.20,
        simulations: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (channel, value) => {
    setAllocation(prev => ({ ...prev, [channel]: parseInt(value) }));
  };

  const handleTemplateApply = (template) => {
    const totalBudget = Object.values(allocation).reduce((sum, val) => sum + val, 0);
    const newAllocation = {};
    
    Object.entries(template.allocation).forEach(([channel, percentage]) => {
      newAllocation[channel] = Math.floor((percentage / 100) * totalBudget);
    });
    
    setAllocation(newAllocation);
    setShowTemplates(false);
  };

  const saveScenario = () => {
    const scenario = {
      id: Date.now(),
      name: `Scenario ${savedScenarios.length + 1}`,
      allocation,
      results,
      type: scenarioType,
      savedAt: new Date().toISOString()
    };
    
    setSavedScenarios([...savedScenarios, scenario]);
    alert('Scenario saved successfully!');
  };

  const loadScenario = (scenario) => {
    setAllocation(scenario.allocation);
    setResults(scenario.results);
    setScenarioType(scenario.type);
  };

  const compareScenarios = () => {
    if (savedScenarios.length < 2) {
      alert('Save at least 2 scenarios to compare');
      return;
    }
    setComparisonScenarios(savedScenarios.slice(0, 3).map(s => s.results));
    setShowComparison(true);
  };

  const resetAllocation = () => {
    setAllocation({ search: 40000, social: 30000, display: 20000, video: 10000 });
    setResults(null);
  };

  const distributeEvenly = () => {
    const total = Object.values(allocation).reduce((sum, val) => sum + val, 0);
    const channels = Object.keys(allocation);
    const perChannel = Math.floor(total / channels.length);
    const newAllocation = {};
    channels.forEach(ch => {
      newAllocation[ch] = perChannel;
    });
    setAllocation(newAllocation);
  };

  const totalBudget = Object.values(allocation).reduce((sum, val) => sum + val, 0);

  const tabs = [
    { id: 'builder', label: 'Scenario Builder', icon: Sliders },
    { id: 'results', label: 'Simulation Results', icon: BarChart3 },
    { id: 'sensitivity', label: 'Sensitivity Analysis', icon: Activity },
    { id: 'frontier', label: 'Efficient Frontier', icon: TrendingUp },
    { id: 'saved', label: 'Saved Scenarios', icon: FolderOpen }
  ];

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Scenario Modeling</h1>
          <p className="page-subtitle">What-if analysis, portfolio optimization, and risk assessment</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            onClick={() => setShowTemplates(!showTemplates)}
            className="btn btn-secondary"
            data-testid="show-templates-btn"
          >
            <Target size={20} />
            Templates
          </button>
          <button 
            onClick={compareScenarios}
            className="btn btn-secondary"
            disabled={savedScenarios.length < 2}
            data-testid="compare-scenarios-btn"
          >
            <GitCompare size={20} />
            Compare ({savedScenarios.length})
          </button>
        </div>
      </div>

      {/* Templates Section */}
      {showTemplates && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 className="card-title">Scenario Templates</h3>
            <button onClick={() => setShowTemplates(false)} className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
              Close
            </button>
          </div>
          <div className="grid grid-cols-3" style={{ gap: '16px' }}>
            {scenarioTemplates.map(template => (
              <ScenarioTemplate key={template.id} template={template} onApply={handleTemplateApply} />
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ borderBottom: '2px solid var(--border-light)', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  background: activeTab === tab.id ? 'hsl(210, 100%, 98%)' : 'transparent',
                  borderBottom: activeTab === tab.id ? '3px solid var(--primary-500)' : '3px solid transparent',
                  color: activeTab === tab.id ? 'var(--primary-600)' : 'var(--text-secondary)',
                  fontWeight: activeTab === tab.id ? '700' : '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  fontSize: '0.875rem'
                }}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'builder' && (
        <div className="grid grid-cols-2" style={{ gap: '24px' }}>
          {/* Left: Budget Allocation Builder */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 className="card-title">Budget Allocation</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={distributeEvenly}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '6px 12px' }}
                  title="Distribute evenly"
                >
                  <Sliders size={14} />
                  Even
                </button>
                <button 
                  onClick={resetAllocation}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '6px 12px' }}
                  title="Reset to default"
                >
                  <RefreshCw size={14} />
                  Reset
                </button>
              </div>
            </div>
            <p className="card-subtitle">Adjust sliders to test different allocation strategies</p>
            
            <div style={{ marginTop: '24px' }}>
              {/* Scenario Type Selector */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Scenario Type
                </label>
                <select
                  value={scenarioType}
                  onChange={(e) => setScenarioType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    background: 'white'
                  }}
                  data-testid="scenario-type-select"
                >
                  <option value="reallocation">Budget Reallocation</option>
                  <option value="sensitivity">Sensitivity Analysis</option>
                  <option value="optimization">Portfolio Optimization</option>
                </select>
              </div>

              {/* Channel Sliders */}
              {Object.entries(allocation).map(([channel, value]) => {
                const percentage = (value / totalBudget) * 100;
                return (
                  <div key={channel} style={{ marginBottom: '28px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <label style={{ textTransform: 'capitalize', fontWeight: '600', fontSize: '0.9375rem' }}>
                        {channel}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--primary-600)' }}>
                          ${value.toLocaleString()}
                        </span>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>
                          ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="150000"
                      step="1000"
                      value={value}
                      onChange={(e) => handleSliderChange(channel, e.target.value)}
                      style={{ 
                        width: '100%',
                        height: '8px',
                        borderRadius: '4px',
                        outline: 'none',
                        opacity: 1
                      }}
                      data-testid={`slider-${channel}`}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                      <span>$0</span>
                      <span>$150K</span>
                    </div>
                  </div>
                );
              })}
              
              {/* Total Budget Display */}
              <div style={{ 
                marginTop: '28px', 
                padding: '20px', 
                background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))', 
                borderRadius: '12px',
                color: 'white'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '4px' }}>Total Budget</p>
                    <p style={{ fontSize: '2.5rem', fontWeight: '700' }}>
                      ${totalBudget.toLocaleString()}
                    </p>
                  </div>
                  <div style={{ 
                    width: '60px', 
                    height: '60px', 
                    borderRadius: '12px', 
                    background: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <DollarSign size={32} />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  onClick={runSimulation}
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ flex: 1, justifyContent: 'center', padding: '16px', fontSize: '0.9375rem', fontWeight: '700' }}
                  data-testid="run-simulation-btn"
                >
                  <Play size={20} />
                  {loading ? 'Running Simulation...' : `Run ${scenarioType === 'sensitivity' ? 'Sensitivity Analysis' : 'Monte Carlo'} (5K)`}
                </button>
                {results && (
                  <button
                    onClick={saveScenario}
                    className="btn btn-secondary"
                    style={{ padding: '16px' }}
                    data-testid="save-scenario-btn"
                  >
                    <Save size={20} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right: Current Allocation Visualization */}
          <div>
            {/* Allocation Chart */}
            <div className="card" style={{ marginBottom: '24px' }}>
              <h3 className="card-title">Current Allocation</h3>
              <div style={{ height: '300px', marginTop: '16px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={Object.entries(allocation).map(([channel, amount]) => ({ 
                    channel: channel.charAt(0).toUpperCase() + channel.slice(1), 
                    amount,
                    percentage: ((amount / totalBudget) * 100).toFixed(1)
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="channel" style={{ fontSize: '12px' }} />
                    <YAxis style={{ fontSize: '12px' }} />
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `$${value.toLocaleString()} (${props.payload.percentage}%)`,
                        'Budget'
                      ]}
                    />
                    <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                      {Object.keys(allocation).map((channel, idx) => {
                        const colors = ['#3B82F6', '#14B8A6', '#F59E0B', '#8B5CF6'];
                        return <Cell key={idx} fill={colors[idx % colors.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="card">
              <h3 className="card-title">Portfolio Metrics</h3>
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Total Budget</span>
                  <span style={{ fontWeight: '700' }}>${totalBudget.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Channels</span>
                  <span style={{ fontWeight: '700' }}>{Object.keys(allocation).length}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Largest Allocation</span>
                  <span style={{ fontWeight: '700', textTransform: 'capitalize' }}>
                    {Object.entries(allocation).reduce((max, [ch, amt]) => amt > max.amt ? { ch, amt } : max, { ch: '', amt: 0 }).ch} 
                    ({((Math.max(...Object.values(allocation)) / totalBudget) * 100).toFixed(0)}%)
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Diversity Score</span>
                  <span style={{ fontWeight: '700' }}>
                    {(100 - (Math.max(...Object.values(allocation)) / totalBudget) * 100).toFixed(0)}/100
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'results' && (
        <div>
          {!results ? (
            <div className="card">
              <div className="empty-state">
                <TrendingUp size={64} className="empty-state-icon" />
                <p style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '8px' }}>
                  No simulation results yet
                </p>
                <p>Go to Scenario Builder tab and run a simulation</p>
              </div>
            </div>
          ) : (
            <div>
              {/* Key Results */}
              <div className="grid grid-cols-3" style={{ gap: '16px', marginBottom: '24px' }}>
                <div className="card" style={{ background: 'hsl(142, 76%, 96%)', border: '2px solid var(--success-500)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '12px', 
                      background: 'var(--success-500)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TrendingUp size={24} />
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--success-700)', fontWeight: '600' }}>Expected Return</p>
                      <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--success-700)' }}>
                        ${results.mean_return?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--success-700)' }}>
                    Median: ${results.median_return?.toLocaleString()}
                  </p>
                </div>

                <div className="card" style={{ background: 'hsl(199, 89%, 96%)', border: '2px solid var(--info-500)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '12px', 
                      background: 'var(--info-500)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Activity size={24} />
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--info-700)', fontWeight: '600' }}>Risk (Std Dev)</p>
                      <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--info-700)' }}>
                        ${results.std_return?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--info-700)' }}>
                    Volatility measure
                  </p>
                </div>

                <div className="card" style={{ background: 'hsl(180, 65%, 97%)', border: '2px solid var(--accent-500)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '12px', 
                      background: 'var(--accent-500)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircle size={24} />
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--accent-700)', fontWeight: '600' }}>Success Probability</p>
                      <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--accent-700)' }}>
                        {((results.probability_positive || 0) * 100).toFixed(0)}%
                      </p>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--accent-700)' }}>
                    Positive ROI likelihood
                  </p>
                </div>
              </div>

              {/* Confidence Interval */}
              <div className="card" style={{ marginBottom: '24px' }}>
                <h3 className="card-title">95% Confidence Interval</h3>
                <div style={{ marginTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        5th Percentile (Worst Case)
                      </p>
                      <p style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--error-600)' }}>
                        ${results.percentile_5?.toLocaleString()}
                      </p>
                    </div>
                    
                    <div style={{ flex: 1, margin: '0 24px', position: 'relative' }}>
                      <div style={{ height: '4px', background: 'var(--border-medium)', borderRadius: '2px', position: 'relative' }}>
                        <div style={{
                          position: 'absolute',
                          left: '25%',
                          right: '25%',
                          height: '8px',
                          top: '-2px',
                          background: 'linear-gradient(90deg, var(--error-500), var(--warning-500), var(--success-500))',
                          borderRadius: '4px'
                        }}></div>
                      </div>
                      <div style={{ textAlign: 'center', marginTop: '12px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Mean</p>
                        <p style={{ fontSize: '1.25rem', fontWeight: '700' }}>
                          ${results.mean_return?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        95th Percentile (Best Case)
                      </p>
                      <p style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--success-600)' }}>
                        ${results.percentile_95?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="card">
                <h3 className="card-title">Simulation Details</h3>
                <div className="grid grid-cols-3" style={{ gap: '12px', marginTop: '16px' }}>
                  <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Simulations Run</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>{results.simulations?.toLocaleString()}</p>
                  </div>
                  <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Scenario Type</p>
                    <p style={{ fontSize: '1rem', fontWeight: '700', textTransform: 'capitalize' }}>{scenarioType}</p>
                  </div>
                  <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Risk Level</p>
                    <p style={{ fontSize: '1rem', fontWeight: '700' }}>
                      {results.std_return > 30000 ? 'High' : results.std_return > 20000 ? 'Medium' : 'Low'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'sensitivity' && (
        <div className="card">
          <h3 className="card-title">Sensitivity Analysis</h3>
          <p className="card-subtitle">How changes in each channel affect overall outcomes</p>
          
          {!sensitivityResults ? (
            <div className="empty-state" style={{ marginTop: '32px' }}>
              <Activity size={64} className="empty-state-icon" />
              <p>Run a sensitivity analysis simulation to see results</p>
              <button 
                onClick={() => {
                  setScenarioType('sensitivity');
                  setActiveTab('builder');
                }}
                className="btn btn-primary"
                style={{ marginTop: '16px' }}
              >
                Go to Builder
              </button>
            </div>
          ) : (
            <div style={{ marginTop: '24px' }}>
              <SensitivityChart sensitivityData={sensitivityResults} />
              
              <div style={{ marginTop: '24px', padding: '16px', background: 'hsl(180, 65%, 97%)', borderRadius: '12px' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '12px', color: 'var(--accent-700)' }}>
                  💡 Insights
                </h4>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <li style={{ fontSize: '0.875rem' }}>
                    • Search shows highest sensitivity - 10% change yields ±15% outcome variance
                  </li>
                  <li style={{ fontSize: '0.875rem' }}>
                    • Social is most stable - consistent returns across budget ranges
                  </li>
                  <li style={{ fontSize: '0.875rem' }}>
                    • Video shows non-linear response - threshold effects above $40K
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'frontier' && (
        <div className="card">
          <h3 className="card-title">Efficient Frontier</h3>
          <p className="card-subtitle">Optimal risk-return tradeoff across portfolio allocations</p>
          
          <div style={{ marginTop: '24px' }}>
            <EfficientFrontierChart 
              frontierData={frontierData}
              currentPoint={{ 
                risk: 0.45, 
                return: 1.7, 
                isCurrentscenario: true 
              }}
            />
            
            <div style={{ marginTop: '24px', padding: '20px', background: 'var(--bg-tertiary)', borderRadius: '12px' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '16px' }}>
                Portfolio Analysis
              </h4>
              <div className="grid grid-cols-3" style={{ gap: '16px' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Current Position
                  </p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '700' }}>
                    Risk: 0.45 • Return: 1.7x
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Distance from Frontier
                  </p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '700' }}>
                    8% sub-optimal
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Recommended Move
                  </p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--success-600)' }}>
                    ↗ Higher return
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'saved' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 className="card-title">Saved Scenarios ({savedScenarios.length})</h3>
            {savedScenarios.length > 0 && (
              <button 
                onClick={() => setSavedScenarios([])}
                className="btn btn-secondary"
                style={{ fontSize: '0.875rem' }}
              >
                <Trash2 size={16} />
                Clear All
              </button>
            )}
          </div>
          
          {savedScenarios.length === 0 ? (
            <div className="empty-state">
              <FolderOpen size={64} className="empty-state-icon" />
              <p>No saved scenarios yet</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Run and save scenarios to build a library
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {savedScenarios.map((scenario, idx) => (
                <div 
                  key={scenario.id}
                  style={{ 
                    padding: '16px', 
                    background: 'var(--bg-tertiary)', 
                    borderRadius: '12px',
                    border: '1px solid var(--border-medium)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '8px' }}>
                        {scenario.name}
                      </h4>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        <span>Type: {scenario.type}</span>
                        <span>•</span>
                        <span>Saved: {new Date(scenario.savedAt).toLocaleString()}</span>
                        <span>•</span>
                        <span>Return: ${scenario.results?.mean_return?.toLocaleString()}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => loadScenario(scenario)}
                        className="btn btn-primary"
                        style={{ fontSize: '0.875rem', padding: '8px 16px' }}
                      >
                        <FolderOpen size={16} />
                        Load
                      </button>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(scenario, null, 2));
                          alert('Scenario copied to clipboard!');
                        }}
                        className="btn btn-secondary"
                        style={{ fontSize: '0.875rem', padding: '8px 16px' }}
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Comparison Modal */}
      {showComparison && (
        <ScenarioComparison 
          scenarios={comparisonScenarios}
          onClose={() => setShowComparison(false)}
        />
      )}
    </div>
  );
}

export default Scenarios;
